import { Alert, Box, Link } from '@mui/material';
import { useId, useState, type ReactNode } from 'react';
import { DefinitionGrid, InformationPage, PageSections, PillTabs, ResourceState, SectionCard } from '../../../design-system';
import { ExternalCareerSection } from '../profile/ExternalCareerSection';
import { fixtureWorkerForViewer, fixtureWorkerRewards } from '../profile/fixtures';
import type { SelfProfileWorker, WorkerRewards } from '../profile/model';
import { EmployeeProfileSummary } from '../profile/EmployeeProfileSummary';
import { EmployeeProfileRewards } from '../profile/EmployeeProfileRewards';
import { EmployeeProfileAffiliations } from '../profile/EmployeeProfileAffiliations';
import { PersonFamilySection } from '../profile/PersonFamilySection';
import type { PeopleProfilePort } from '../profile/port';
import { profileDateValue, profileTextValue } from '../profile/profileDisplay';
import { useAsyncResource, type AsyncResource } from '../../../shared/useAsyncResource';

export type EmployeeProfileViewer = {
  workerId: string;
  workerReference?: string;
  scopes: readonly string[];
  impersonating: boolean;
  rewardsAvailable?: boolean;
};

export type EmployeeProfilePageProps = {
  port: PeopleProfilePort;
  viewer: EmployeeProfileViewer;
  fixtureMode: boolean;
  onProfilePhotoChanged?: () => void;
};

type ProfileTab = 'overview' | 'family' | 'career' | 'rewards';

function ResourceBoundary<T>({ resource, children, errorTitle, errorMessage, loadingVariant = 'profile' }: {
  resource: AsyncResource<T>;
  children: (data: T) => ReactNode;
  errorTitle?: ReactNode;
  errorMessage?: ReactNode;
  loadingVariant?: 'profile' | 'cards';
}) {
  return <ResourceState loading={resource.loading} loadingVariant={loadingVariant} error={resource.error ? errorMessage ?? resource.error.message : undefined} errorTitle={errorTitle} empty={false} onRetry={resource.reload}>{resource.data === undefined ? null : children(resource.data)}</ResourceState>;
}

export function EmployeeProfilePage({ port, viewer, fixtureMode, onProfilePhotoChanged }: EmployeeProfilePageProps) {
  const tabId = useId();
  const [tab, setTab] = useState<ProfileTab>('overview');
  const resource = useAsyncResource(
    () => fixtureMode
      ? Promise.resolve(fixtureWorkerForViewer(viewer.workerId, viewer.impersonating))
      : viewer.workerId ? port.getWorker(viewer.workerReference ?? viewer.workerId) : Promise.resolve(undefined),
    [fixtureMode, port, viewer.impersonating, viewer.workerId, viewer.workerReference],
    '정보를 불러오지 못했습니다.',
  );
  const scopes = new Set(viewer.scopes);
  const canReadRewards = viewer.rewardsAvailable !== false && ((fixtureMode && !viewer.impersonating) || scopes.has('rewards.read'));
  const rewards = useAsyncResource<WorkerRewards | undefined>(
    () => !canReadRewards
      ? Promise.resolve(undefined)
      : fixtureMode
        ? Promise.resolve(fixtureWorkerRewards)
        : viewer.workerId ? port.getWorkerRewards(viewer.workerId) : Promise.resolve(undefined),
    [canReadRewards, fixtureMode, port, viewer.workerId],
    '정보를 불러오지 못했습니다.',
  );
  const canReadFamily = fixtureMode || scopes.has('person.family.read');
  const canWriteFamily = !viewer.impersonating && (fixtureMode || scopes.has('person.family.write'));
  const canReadCareer = fixtureMode || scopes.has('person.career.read');
  const canWriteCareer = !viewer.impersonating && (fixtureMode || scopes.has('person.career.write'));

  const tabs: Array<{ value: ProfileTab; label: string }> = [
    { value: 'overview', label: '기본 정보' },
    ...(canReadFamily ? [{ value: 'family' as const, label: '가족' }] : []),
    ...(canReadCareer ? [{ value: 'career' as const, label: '외부 경력' }] : []),
    ...(canReadRewards ? [{ value: 'rewards' as const, label: '계약·보상' }] : []),
  ];
  const selectedTab = tabs.some((item) => item.value === tab) ? tab : 'overview';
  const panelProps = (value: ProfileTab) => ({
    role: 'tabpanel', id: `${tabId}-${value}-panel`,
    'aria-labelledby': `${tabId}-${value}-tab`, hidden: selectedTab !== value,
    tabIndex: 0,
  });

  return <InformationPage title="내 정보" width="wide">
    <PageSections spacing="grouped">
      <ResourceBoundary resource={resource}>{(worker) => worker ? <EmployeeProfileSummary
        worker={worker} port={port} fixtureMode={fixtureMode} readOnly={viewer.impersonating}
        onPhotoChanged={() => { if (!fixtureMode) resource.reload(); onProfilePhotoChanged?.(); }}
      /> : <Alert severity="warning">현재 계정에 연결된 Worker가 없습니다.</Alert>}</ResourceBoundary>
      <PillTabs
        value={selectedTab}
        onChange={setTab}
        ariaLabel="내 정보 항목"
        items={tabs.map((item) => ({ ...item, tabId: `${tabId}-${item.value}-tab`, panelId: `${tabId}-${item.value}-panel` }))}
      />
      <Box {...panelProps('overview')}>
        {resource.data && <EmployeeProfileOverview worker={resource.data} />}
      </Box>
      {canReadFamily && <Box {...panelProps('family')}>
        <PersonFamilySection port={port} canRead={canReadFamily} canWrite={canWriteFamily} fixtureMode={fixtureMode} />
      </Box>}
      {canReadCareer && <Box {...panelProps('career')}>
        <ExternalCareerSection port={port} canRead={canReadCareer} canWrite={canWriteCareer} fixtureMode={fixtureMode} />
      </Box>}
      {canReadRewards && <Box {...panelProps('rewards')}>
        <ResourceBoundary resource={rewards} loadingVariant="cards" errorTitle="계약 정보를 확인할 수 없습니다." errorMessage="현재 계정의 권한 또는 연결 정보를 확인한 뒤 다시 시도해 주세요.">
          {(data) => data ? <EmployeeProfileRewards data={data} /> : null}
        </ResourceBoundary>
      </Box>}
    </PageSections>
  </InformationPage>;
}

function EmployeeProfileOverview({ worker }: { worker: SelfProfileWorker }) {
  const email = profileTextValue(worker.person?.email);
  return <PageSections>
    <EmployeeProfileAffiliations worker={worker} />
    <SectionCard title="재직 정보" bodyPadding="standard">
      <DefinitionGrid columns={3} testId="employee-employment-information" items={[
        { id: 'worker-number', label: '사번', value: profileTextValue(worker.workerId) },
        { id: 'hire-date', label: '최근 입사일', value: profileDateValue(worker.hireDate) },
        { id: 'original-hire-date', label: '최초 입사일', value: profileDateValue(worker.originalHireDate || worker.hireDate) },
      ]} />
    </SectionCard>
    <SectionCard title="연락처" bodyPadding="standard">
      <DefinitionGrid testId="employee-contact-information" items={[
        { id: 'work-email', label: '회사 이메일', value: email ? <Link href={`mailto:${email}`} underline="hover">{email}</Link> : undefined },
        { id: 'phone', label: '연락처', value: profileTextValue(worker.person?.phone) },
      ]} />
    </SectionCard>
  </PageSections>;
}
